import EditorPageLayout from "../../../../components/layouts/editor-page/editor-page-layout";
import { observer } from "mobx-react-lite";
import { Fragment, useCallback, useEffect, useState } from "react";
import usePage from "../../../../components/layouts/base-page-layout/page-hook";
import { FONT_COLOR } from "../../../../components/fonts/font-props";
import FontBody2 from "../../../../components/fonts/font-body2/font-body2";
import useTranslation from "../../../../translation/hooks/translation-hook";
import emailContentListStore from "../../stores/email-content-list-store";
import { ButtonIcon } from "../../../../components/inputs/button-icon/button-icon";
import useNavigationBlocker from "../../../../hooks/navigation-blocker-hook";
import { ConfirmModal } from "../../../../components/modal/components/confirm-modal/confirm-modal";
import IconVariablesAndEvents from "../../../../components/icons/icon-variables-and-events/icon-variables-and-events";
import { Tooltip, TOOLTIP_VARIANTS } from "../../../../components/tooltip/tooltip";
import EmailTypeCard from "./email-type-card/email-type-card";
import { EMAIL_CATEGORY } from "../../../../services/api/email-content-service";
import currentPortalStore from "../../../../stores/current-portal-store";
import { FEATURES } from "../../../../models/Plan";
import { ExpandCallapseStore } from "../../../../hooks/expand-collapse-hook";
import { useOutletContext } from "react-router-dom";
import ListLayout from "../../../../components/layouts/list-layout/list-layout";
import "./email-type-list.scss";

const EmailContentList:React.FC = observer(() => {
    const { selectedLocale, setTranslatableKeys } = useTranslation();
    const emailsCategoryList = emailContentListStore.emailCategoryList;
    const { setSubTitles } = usePage();
    const [isShowUnsaved, setIsShowUnsaved] = useState(false);
    const [, setHasNotificationIntegration] = useState(false)
    const [, setIntegrationServiceName] = useState('')
    const expandCollapseStore = useOutletContext<ExpandCallapseStore>();

    const hasChanges = (): boolean => {
        for (let emailCategoryStore of emailsCategoryList) {
            for (let emailContentStore of emailCategoryStore.list) {
                if (emailContentStore?.hasChanges()) {
                    return emailContentStore.hasChanges();
                }
            }
        }

        return false;
    }

    const discardChanges = () => {
        for (let emailCategoryStore of emailsCategoryList) {
            for (let emailContentStore of emailCategoryStore.list) {
                if (emailContentStore?.hasChanges()) {
                    emailContentStore?.discardChanges();
                }
            }
        }
    }

    const onSaveClick = () => {
        const promised = [];

        for (let emailCategoryStore of emailsCategoryList) {
            for (let emailContentStore of emailCategoryStore.list) {
                if (emailContentStore?.hasChanges()) {
                    promised.push(emailContentStore.save())
                }
            }
        }

        Promise.all(promised).then(() => {
            unblockNavigation();
        });
    }

    const { retryNavigation, unblockNavigation } = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );

    useEffect(() => {
        setSubTitles(['Emails', 'Content']);
        emailContentListStore.onShowTranslationButton(true);
        for (let emailCategoryStore of emailsCategoryList) {
            for (let emailContentStore of emailCategoryStore.list) {
                emailContentStore.setSelectedLocale(selectedLocale);
            }
        }
    }, [selectedLocale, setSubTitles, emailsCategoryList]);

    useEffect(() => {
        const keys: string[] = [];

        setTranslatableKeys(keys);

        return () => setTranslatableKeys([]);
    }, [setTranslatableKeys]);

    useEffect(() => {
        setHasNotificationIntegration(emailContentListStore.hasNotificationIntegration);
        setIntegrationServiceName(emailContentListStore.integrationServiceName);
    }, []);

    const openInNewTab = (url: string) => {
        window.open(url, '_blank', 'noreferrer');
    };

    const showDrafOrderTranslations = (category: EMAIL_CATEGORY): boolean => {
        const isShopify = currentPortalStore.platform?.getPlatformDisplayName() === 'Shopify';
        return isShopify && category === EMAIL_CATEGORY.returns_email;
    }

    return (
        <>
            <EditorPageLayout toolbar={{
                hasLocalesSelector: true, hasBack: false,
                content: (
                    <div className="email-type-list-toolbar">
                        <Tooltip content={
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                Events & variables
                            </FontBody2>
                        } variant={TOOLTIP_VARIANTS.INFO}>
                            <ButtonIcon Component={() => <IconVariablesAndEvents color="var(--color-accent-1)" />}
                                onClick={() => openInNewTab('variables-and-events')}></ButtonIcon>
                        </Tooltip>
                    </div>
                ),

                saveButton: {
                    hasChanges: hasChanges(),
                    onClick: () => onSaveClick(),
                    disabled: !hasChanges(),
                },
            }}>
                <ListLayout>
                    {
                        emailsCategoryList.map((category) =>
                            <Fragment key={category.category}>
                                <EmailTypeCard
                                    title={category.category}
                                    toolbarContent={""}
                                    events={category.list}
                                    isExpanded={expandCollapseStore.getIsExpanded(category.category)}
                                    onExpandClick={(v: boolean) => expandCollapseStore.setExpanded(category.category, v)}
                                    disabled={
                                        (category.category === EMAIL_CATEGORY.tracking_order_email)
                                        && !currentPortalStore.isFeatureAllowed(FEATURES.ORDER_TRACKING)
                                    }
                                    disabledTooltip={{
                                        content: (
                                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                                The tracking portal feature is <br />
                                                not included in your <br />
                                                subscription plan. To activate <br />
                                                this feature, contact support.
                                            </FontBody2>
                                        )
                                    }}
                                    showDraftOrderTranslations={showDrafOrderTranslations(category.category)}
                                />
                            </Fragment>
                        )
                    }
                </ListLayout>
            </EditorPageLayout>
            {isShowUnsaved &&
                <ConfirmModal
                    cancelButton={{
                        title: 'Cancel',
                        onClick: () => setIsShowUnsaved(false)
                    }}
                    okButton={{
                        title: 'Discard',
                        onClick: () => {
                            discardChanges();
                            setIsShowUnsaved(false);
                            retryNavigation();
                        }
                    }}
                />
            }
        </>
    )
});

export default EmailContentList;



